/** @format */

import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { AiFillHeart } from "react-icons/ai";
import {
  FiClock,
  FiMapPin,
  FiMessageSquare,
  FiShoppingCart,
  FiTruck,
} from "react-icons/fi";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import Carousel from "react-multi-carousel";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import FadeLoader from "react-spinners/FadeLoader";
import "swiper/css";
import "swiper/css/pagination";
import ComposentDetails from "../components/ComposentDetails";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import Ratings from "../components/Ratings";
import VideoPlayer from "../components/VideoPlayer";
import { messageClear } from "../store/reducers/authReducer";
import { add_To_card, add_To_wishlist } from "../store/reducers/cardReducers";
import { add_friend, send_message } from "../store/reducers/chatReducer";
import { get_product_details } from "../store/reducers/homeReducer";
import { useCategories } from "../utils/Categories";
import TrunckText from "../utils/TrunckText";
import { devise, useProductTracking, WhatsAppButton } from "../utils/utils";

const responsive = {
  superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 3 },
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 3 },
  mdtablet: { breakpoint: { max: 991, min: 464 }, items: 3 },
  mobile: { breakpoint: { max: 768, min: 0 }, items: 2 },
  smmobile: { breakpoint: { max: 640, min: 0 }, items: 2 },
  xsmobile: { breakpoint: { max: 440, min: 0 }, items: 2 },
};

const PlayIcon = ({ size = "md" }) => (
  <div
    className={`absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 rounded-xl`}
  >
    <div
      className={`${size === "sm" ? "w-7 h-7" : "w-11 h-11"} flex items-center justify-center bg-white/90 group-hover:bg-white rounded-full shadow-md`}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        className={`${size === "sm" ? "w-3 h-3" : "w-5 h-5"} text-gray-800`}
        fill='currentColor'
        viewBox='0 0 16 16'
      >
        <path d='M4.5 3.5v9l8-4.5-8-4.5z' />
      </svg>
    </div>
  </div>
);

const ProduitsDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    product,
    moreProducts,
    relatedProducts,
    totalReviews,
    reviewsError,
    reviews,
    rating_review,
    parPage,
    reviewsSuccess,
    shippingFee,
    currentPage,
    video,
    seller,
  } = useSelector((state) => state.home);

  const { t } = useTranslation();
  const { customerInfo } = useSelector((state) => state.auth);
  const { successMessage, messageError } = useSelector((state) => state.card);
  const { categories, loading, clientLocation } = useCategories();

  const [state, setState] = React.useState("reviews");
  const [quantity, setQuantity] = React.useState(1);
  const [mainMedia, setMainMedia] = useState({ type: "image", src: "" });
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  //const [receiveMessage] = React.useState("");

  // ✅ useProductTracking appelé au niveau du composant
  const { trackAction } = useProductTracking(product?._id);

  let imagefg = product?.images?.[0]?.url || "";

  useEffect(() => {
    if (!productId) return;
    dispatch(get_product_details(productId));
  }, [productId, dispatch]);

  useEffect(() => {
    if (messageError) dispatch(messageClear());
    if (successMessage) dispatch(messageClear());
  }, [messageError, successMessage, dispatch]);

  if (!product || !Array.isArray(product.variants)) {
    return (
      <div className='w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-gray-500/70 z-[99999]'>
        <FadeLoader />
      </div>
    );
  }

  const getDefaultVariant = () =>
    product.variants?.find((v) => v.variantStock > 0);
  const getFinalVariantForCart = () => selectedVariant || getDefaultVariant();
  const getVariantDiscountedPrice = (variant) => {
    if (!variant) return 0;
    const discount = product.discount || 0;
    return Math.floor(
      variant.variantPrice - (variant.variantPrice * discount) / 100,
    );
  };
  const getStock = () => {
    if (selectedVariant) return selectedVariant.variantStock;
    const def = getDefaultVariant();
    return def ? def.variantStock : product.stock;
  };

  const inc = () =>
    quantity >= getStock() ? toast("Out of stock") : setQuantity((q) => q + 1);
  const dec = () =>
    quantity <= 1 ? toast("Minimum quantity is 1") : setQuantity((q) => q - 1);

  const QuantityInput = () => (
    <input
      type='number'
      min='1'
      max={getStock()}
      value={quantity}
      onChange={(e) => {
        const v = parseInt(e.target.value);
        const s = getStock();
        if (!isNaN(v) && v >= 1 && v <= s) setQuantity(v);
        else toast(`La quantité doit être entre 1 et ${s}`);
      }}
      className='w-12 text-center bg-transparent border-none outline-none font-semibold text-gray-800'
    />
  );

  const uniqueColors = [
    ...new Set(product.variants.map((v) => v.color).filter(Boolean)),
  ];
  const availableSizes = selectedColor
    ? [
        ...new Set(
          product.variants
            .filter((v) => v.color === selectedColor)
            .map((v) => v.size)
            .filter(Boolean),
        ),
      ]
    : [...new Set(product.variants.map((v) => v.size).filter(Boolean))];
  const availableColors = selectedSize
    ? [
        ...new Set(
          product.variants
            .filter((v) => v.size === selectedSize)
            .map((v) => v.color)
            .filter(Boolean),
        ),
      ]
    : uniqueColors;

  const handleColorSelect = (color) => {
    setSelectedColor(color);
    const sizeStillValid = product.variants.some(
      (v) => v.color === color && v.size === selectedSize,
    );
    const newSize = sizeStillValid ? selectedSize : null;
    if (!sizeStillValid) setSelectedSize(null);
    const match = product.variants.find(
      (v) => v.color === color && (!newSize || v.size === newSize),
    );
    if (match) {
      setSelectedVariant(match);
      const url =
        typeof match.variantImage === "string"
          ? match.variantImage
          : match.variantImage?.url;
      if (url) setMainMedia({ type: "image", src: url });
    } else {
      setSelectedVariant(null);
      toast.error(t("Variant Error"));
    }
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    const colorStillValid = product.variants.some(
      (v) => v.size === size && v.color === selectedColor,
    );
    const newColor = colorStillValid ? selectedColor : null;
    if (!colorStillValid) setSelectedColor(null);
    const match = product.variants.find(
      (v) => v.size === size && (!newColor || v.color === newColor),
    );
    if (match) {
      setSelectedVariant(match);
      const url =
        typeof match.variantImage === "string"
          ? match.variantImage
          : match.variantImage?.url;
      if (url) setMainMedia({ type: "image", src: url });
    } else {
      setSelectedVariant(null);
      toast.error(t("Variant Error"));
    }
  };

  // ── Actions avec tracking ─────────────────────────────────

  const add_card = () => {
    if (!customerInfo) {
      navigate("/login");
      return;
    }
    const variant = getFinalVariantForCart();
    if (!variant) {
      dispatch(
        add_To_card({
          customerId: customerInfo.id,
          productId: product._id,
          quantity,
        }),
      );
    } else {
      dispatch(
        add_To_card({
          customerId: customerInfo.id,
          productId: product._id,
          quantity,
          selectedVariant: {
            color: variant.color,
            size: variant.size,
            variantPrice: variant.variantPrice,
            variantImage: variant.variantImage?.url || variant.variantImage,
            variantStock: variant.variantStock,
          },
        }),
      );
    }
    trackAction("addToCart"); // ✅
  };

  const add_wishlist = () => {
    if (customerInfo) {
      dispatch(
        add_To_wishlist({
          customerId: customerInfo.id,
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.images[0],
          discount: product.discount,
          rating: product.rating,
          slug: product.slug,
        }),
      );
      trackAction("addToWishlist"); // ✅
    }
  };

  const buy_now = () => {
    if (!customerInfo) {
      toast(t("Please log in"));
      return;
    }
    const variant = getFinalVariantForCart();
    const discount = product.discount || 0;
    const unitPrice = variant
      ? getVariantDiscountedPrice(variant)
      : Math.floor(product.price - (product.price * discount) / 100);
    let fee = 0;
    if (product.deliveryType === "fixed")
      fee = (product.deliveryFee || 0) * parseInt(quantity);
    const objetProduct = [
      {
        sellerId: product.sellerId,
        shopName: product.shopName,
        price: parseInt(quantity) * unitPrice,
        products: [
          {
            quantity,
            ...(variant && {
              selectedVariant: {
                color: variant.color,
                size: variant.size,
                variantPrice: variant.variantPrice,
                variantImage:
                  typeof variant.variantImage === "string"
                    ? variant.variantImage
                    : variant.variantImage?.url,
                variantStock: variant.variantStock,
              },
            }),
            productInfo: { ...product, unitPrice },
          },
        ],
      },
    ];
    trackAction("addToCart"); // ✅ buy_now = achat immédiat
    navigate("/shipping", {
      state: {
        mode: "buynow",
        products: objetProduct,
        price: unitPrice * parseInt(quantity),
        Shipping_fees: fee,
        coupon: 0,
        items: quantity,
      },
    });
  };

  const sendProductInfoMessage = async (prod) => {
    if (!customerInfo) return navigate("/login");
    const variant = getFinalVariantForCart();
    const discount = prod.discount || 0;
    const price = variant
      ? getVariantDiscountedPrice(variant)
      : Math.floor(prod.price - (prod.price * discount) / 100);
    const payload = {
      senderName: customerInfo.name,
      customerId: customerInfo.id,
      sellerId: prod.sellerId,
      messageType: "product",
      message: "ce produit est toujours disponible ?",
      productInfo: {
        image:
          variant?.variantImage?.url ||
          variant?.variantImage ||
          prod.images[0]?.url ||
          prod.images[0],
        title: prod.name,
        price,
        stock: variant?.variantStock || prod.stock,
        productId: prod._id,
        color: variant?.color,
        size: variant?.size,
      },
    };
    await dispatch(
      add_friend({
        sellerId: prod.sellerId || "",
        customerId: customerInfo.id,
      }),
    );
    dispatch({ type: "chat/updateFdMessages", payload });
    dispatch(send_message(payload))
      .unwrap()
      .then(() => {
        trackAction("sendMessage"); // ✅
        navigate(`/dashboard/my_chats/${prod.sellerId}`);
      })
      .catch((err) => {
        toast.error(t("Erreur lors de l'envoi du message"));
        console.error(err);
      });
  };

  const PriceBlock = () => {
    if (selectedVariant)
      return (
        <div className='flex items-baseline gap-3'>
          <span className='text-2xl font-bold text-gray-900'>
            {getVariantDiscountedPrice(selectedVariant)} {devise}
          </span>
          <span className='text-base text-gray-400 line-through'>
            {selectedVariant.variantPrice} {devise}
          </span>
          {product.discount > 0 && (
            <span className='text-sm font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full'>
              -{product.discount}%
            </span>
          )}
        </div>
      );
    if (product.discount)
      return (
        <div className='flex items-baseline gap-3'>
          <span className='text-2xl font-bold text-gray-900'>
            {Math.floor(
              (parseFloat(product.price) * (100 - product.discount)) / 100,
            )}{" "}
            {devise}
          </span>
          <span className='text-base text-gray-400 line-through'>
            {product.price} {devise}
          </span>
          <span className='text-sm font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full'>
            -{product.discount}%
          </span>
        </div>
      );
    return (
      <span className='text-2xl font-bold text-gray-900'>
        {product.price} {devise}
      </span>
    );
  };

  const stockCount = selectedVariant
    ? selectedVariant.variantStock
    : product.stock;
  const isInStock = stockCount > 0;

  return (
    <div className='select-none font-helvetica bg-gray-50 min-h-screen'>
      {loading && (
        <div className='w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-gray-500/70 z-[99999]'>
          <FadeLoader />
        </div>
      )}

      <Headers categories={categories} clientLocation={clientLocation} />

      <div
        style={{
          backgroundImage: `url(${imagefg || "http://shop-ship.com/wp-content/uploads/2019/12/Shop-Ship-background-500x281.png"})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
          maxHeight: "200px",
          minHeight: "90px",
        }}
        className='relative sm:hidden mt-6'
      >
        <div className='absolute inset-0 bg-gradient-to-r from-blue-900/60 to-blue-700/40 backdrop-blur-[1px]'>
          <div className='w-[85%] mx-auto h-full flex items-center'>
            <h1 className='text-3xl text-white drop-shadow'>
              BOUTIQUE : <strong>{product?.shopName}</strong>
            </h1>
          </div>
        </div>
      </div>

      <div className='bg-white border-b border-gray-100 py-3'>
        <div className='w-[85%] md:w-[90%] sm:w-[95%] mx-auto flex items-center gap-1 text-sm text-gray-500'>
          <Link
            to='/'
            className='hover:text-blue-600 transition-colors truncate max-w-[80px]'
          >
            {t("Home")}
          </Link>
          <MdOutlineKeyboardArrowRight className='flex-shrink-0' />
          <Link
            to='/'
            className='hover:text-blue-600 transition-colors truncate max-w-[120px]'
            title={product.category}
          >
            {product.category}
          </Link>
          <MdOutlineKeyboardArrowRight className='flex-shrink-0' />
          <span
            className='text-gray-800 font-medium truncate max-w-[200px]'
            title={product.name}
          >
            {product.name}
          </span>
        </div>
      </div>

      <section className='py-8'>
        <div className='w-[85%] md:w-[90%] sm:w-[95%] mx-auto'>
          <div className='grid grid-cols-2 md-lg:grid-cols-1 gap-10'>
            {/* ── Left: Media ── */}
            <div className='flex flex-col md-lg:flex-row gap-4'>
              <div className='flex-col gap-2 max-h-[500px] overflow-y-auto md-lg:flex hidden w-[90px]'>
                {video?.url && (
                  <div
                    onClick={() =>
                      setMainMedia({ type: "video", src: video.url })
                    }
                    className='cursor-pointer border-2 border-transparent hover:border-blue-500 rounded-lg overflow-hidden relative group transition-all'
                  >
                    <video
                      src={video.url}
                      className='h-[70px] w-[80px] object-cover opacity-80 group-hover:opacity-100'
                      preload='metadata'
                      muted
                    />
                    <PlayIcon size='sm' />
                  </div>
                )}
                {product.images?.map((item, index) => (
                  <div
                    key={index}
                    onClick={() =>
                      setMainMedia({ type: "image", src: item.url })
                    }
                    className={`cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${mainMedia.src === item.url ? "border-blue-500" : "border-transparent hover:border-gray-300"}`}
                  >
                    <img
                      src={item.url}
                      alt=''
                      className='h-[70px] w-[80px] object-contain'
                    />
                  </div>
                ))}
              </div>

              {/* ✅ Clic image principale → track viewDetails */}
              <div
                className='flex-1 bg-white rounded-2xl border border-gray-100 shadow-sm flex justify-center items-center p-6 min-h-[380px] cursor-pointer'
                onClick={() => trackAction("viewDetails")}
              >
                {mainMedia.type === "image" ? (
                  <img
                    className='h-[460px] w-full md:h-[350px] sm:h-[240px] object-contain transition-all duration-300'
                    src={mainMedia.src || product?.images?.[0]?.url}
                    alt={product.name}
                  />
                ) : (
                  <VideoPlayer src={video.url} />
                )}
              </div>

              <div className='md-lg:hidden mt-3 flex gap-3 items-center'>
                <div className='w-[80%]'>
                  <Carousel
                    swipeable
                    draggable={false}
                    responsive={responsive}
                    ssr
                    infinite
                    autoPlay
                    autoPlaySpeed={2200}
                    arrows
                    keyBoardControl
                    transitionDuration={500}
                  >
                    {product.images?.map((item, index) => (
                      <div
                        key={index}
                        onClick={() =>
                          setMainMedia({ type: "image", src: item.url })
                        }
                        className={`cursor-pointer mx-1 rounded-lg overflow-hidden border-2 transition-all ${mainMedia.src === item.url ? "border-blue-500" : "border-transparent hover:border-gray-300"}`}
                      >
                        <img
                          src={item.url}
                          alt=''
                          className='h-[100px] w-full object-contain bg-white'
                        />
                      </div>
                    ))}
                  </Carousel>
                </div>
                {video?.url && (
                  <div
                    onClick={() =>
                      setMainMedia({ type: "video", src: video.url })
                    }
                    className='cursor-pointer w-[22%] relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all'
                  >
                    <video
                      src={video.url}
                      className='h-[100px] w-full object-cover opacity-80 group-hover:opacity-100'
                      preload='metadata'
                      muted
                    />
                    <PlayIcon />
                  </div>
                )}
              </div>
            </div>

            {/* ── Right: Details ── */}
            <div className='flex flex-col gap-5'>
              <h1 className='text-2xl md:text-xl font-bold text-gray-900 leading-snug'>
                {product.name}
              </h1>

              <div className='flex items-center gap-3'>
                <div className='flex text-lg'>
                  <Ratings ratings={product.rating} />
                </div>
                <span className='text-sm text-blue-600 font-medium'>
                  ({totalReviews} {t("Reviews")})
                </span>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isInStock ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}
                >
                  {isInStock
                    ? `${stockCount} ${t("In Stock")}`
                    : t("Out of Stock")}
                </span>
              </div>

              <div className='bg-orange-50 rounded-xl px-4 py-3'>
                <PriceBlock />
              </div>

              <div className='md-lg:hidden text-sm text-gray-600 leading-relaxed'>
                <TrunckText text={product.description} maxLength={500} />
              </div>

              <hr className='border-green-200 h-3' />

              {availableColors.length > 0 && (
                <div className='flex flex-col gap-2'>
                  <p className='text-sm font-semibold text-gray-700'>
                    {t("Color")}
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    {availableColors.map((color, index) => {
                      const variantImg = product.variants.find(
                        (v) => v.color === color,
                      );
                      return (
                        <div
                          key={index}
                          onClick={() => handleColorSelect(color, variantImg)}
                          className={`cursor-pointer rounded-xl border-2 px-2 py-1 transition-all text-sm font-medium
                            ${selectedColor === color ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 hover:border-gray-400 text-gray-700"}`}
                        >
                          {variantImg?.variantImage ? (
                            <div className='flex items-center gap-1.5'>
                              <img
                                src={
                                  variantImg.variantImage.url ||
                                  variantImg.variantImage
                                }
                                alt={color}
                                className='h-9 w-9 object-contain rounded'
                              />
                              <span>{color}</span>
                            </div>
                          ) : (
                            <span>{color}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {availableSizes.length > 0 && (
                <div className='flex flex-col gap-2'>
                  <p className='text-sm font-semibold text-gray-700'>
                    {t("Sizes")}
                  </p>
                  <div className='flex flex-wrap gap-2'>
                    {availableSizes.map((size, index) => (
                      <div
                        key={index}
                        onClick={() => handleSizeSelect(size)}
                        className={`cursor-pointer px-4 py-1.5 rounded-xl border-2 text-sm font-medium transition-all
                          ${selectedSize === size ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 hover:border-gray-400 text-gray-700"}`}
                      >
                        {size}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isInStock && (
                <div className='flex items-center gap-3'>
                  <div className='flex items-center bg-gray-100 rounded-xl overflow-hidden border border-gray-200'>
                    <button
                      onClick={dec}
                      className='px-4 py-3 text-gray-700 hover:bg-gray-200 font-bold text-lg transition-colors'
                    >
                      −
                    </button>
                    <QuantityInput />
                    <button
                      onClick={inc}
                      className='px-4 py-3 text-gray-700 hover:bg-gray-200 font-bold text-lg transition-colors'
                    >
                      +
                    </button>
                  </div>
                  {/* ✅ add_card() contient déjà trackAction("addToCart") */}
                  <button
                    onClick={add_card}
                    className='flex-1 flex items-center justify-center gap-2 h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors shadow-sm hover:shadow-md'
                  >
                    <FiShoppingCart size={18} />
                    {t("Add to Cart")}
                  </button>
                  {/* ✅ add_wishlist() contient déjà trackAction("addToWishlist") */}
                  <button
                    onClick={add_wishlist}
                    className='w-12 h-12 flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-red-500 border border-pink-200 transition-colors'
                    title={t("Add to wishlist")}
                  >
                    <AiFillHeart size={20} />
                  </button>
                </div>
              )}

              <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col gap-2.5 text-sm'>
                <div className='flex items-center justify-between text-gray-700'>
                  <span className='flex items-center gap-2 text-gray-500'>
                    <FiTruck size={15} /> {t("Delivery Fees")}
                  </span>
                  <span className='font-medium'>
                    {product.deliveryType === "free"
                      ? t("Free delivery")
                      : product.deliveryType === "negotiable"
                        ? t("Negotiate with the seller")
                        : `${shippingFee} ${devise} ${t("per unit")}`}
                  </span>
                </div>
                <div className='flex items-center justify-between text-gray-700'>
                  <span className='flex items-center gap-2 text-gray-500'>
                    <FiClock size={15} /> {t("Delivery Time")}
                  </span>
                  <span className='font-medium'>
                    {product.estimatedDeliveryTime}
                  </span>
                </div>
                <div className='flex items-center justify-between text-gray-700'>
                  <span className='flex items-center gap-2 text-gray-500'>
                    <FiMapPin size={15} /> {t("Delivery To")}
                  </span>
                  <span className='font-medium'>
                    {clientLocation.city}, {clientLocation.country}
                  </span>
                </div>
              </div>

              <div className='flex flex-wrap gap-3'>
                {isInStock && (
                  // ✅ buy_now() contient déjà trackAction("addToCart")
                  <button
                    onClick={buy_now}
                    className='flex-1 h-12 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl uppercase tracking-wide transition-colors shadow-sm hover:shadow-md text-sm sm:text-[10px]'
                  >
                    {t("BUY NOW")}
                  </button>
                )}
                {/* ✅ sendProductInfoMessage() contient déjà trackAction("sendMessage") */}
                <button
                  onClick={() => sendProductInfoMessage(product)}
                  className='flex-1 h-12 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 uppercase text-white font-semibold rounded-xl transition-colors shadow-sm hover:shadow-md text-sm sm:text-[10px]'
                >
                  <FiMessageSquare size={16} className='sm:hidden' />
                  {t("Chat with Seller")}
                </button>
                {/* ✅ track whatsapp via onClickCallback */}
                <WhatsAppButton
                  buttonName='WHATSAPP'
                  sellerPhone={seller?.shopInfo?.telephone}
                  message={{ messageType: "product", productInfo: product }}
                  productId={product._id}
                  onClickCallback={() => trackAction("whatsapp")}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <ComposentDetails
        setState={setState}
        state={state}
        product={product}
        moreProducts={moreProducts}
        relatedProducts={relatedProducts}
        customerInfo={customerInfo}
        reviewsSuccess={reviewsSuccess}
        rating_review={rating_review}
        reviews={reviews}
        totalReviews={totalReviews}
        parPage={parPage}
        reviewsError={reviewsError}
        currentPage={currentPage}
        t={t}
      />
      <Footer />
    </div>
  );
};

export default ProduitsDetails;
